import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  /**
   * Structured data is now rendered server-side via the route `head()` (see
   * `src/lib/seo-head.ts` + `src/lib/schemas.ts`), so it is no longer injected
   * here. The prop is kept for backwards compatibility with existing pages.
   */
  schema?: unknown;
  canonical?: string;
}

/**
 * Lightweight client-side title/meta updater.
 *
 * Per-page title, description, canonical, Open Graph and Twitter tags, plus
 * JSON-LD structured data are all rendered in the server HTML via each route's
 * `head()`. This component only refreshes the existing (already
 * server-rendered) title/description/OG/Twitter/canonical tags when a page's
 * client state changes the document title — e.g. a booking "success" screen.
 * It never appends new tags, so it cannot duplicate the server-rendered head.
 */
export default function SEO({ title, description, canonical }: SEOProps) {
  useEffect(() => {
    // Update Title
    document.title = title;

    // Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", description);

    // Update OG Title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);

    // Update OG Description
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", description);

    // Update Twitter Title
    let twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", title);

    // Update Twitter Description
    let twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", description);

    // Update Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute(
        "href",
        canonical ||
          `https://dsbusinessgrowth.co.uk${window.location.pathname}`,
      );
    }
  }, [title, description, canonical]);

  return null;
}
